// DeliverableFilterChips — segmented filter bar for the student program
// Deliverables tab. Five chips: All / Active / In review / Complete / Locked,
// each with a count. The active chip is forest-filled with linen text; the
// rest are transparent with a sand-d border and muted text. Click swaps the
// filter state in the parent.

import { Platform, Pressable, Text, View } from "react-native";
import { COLORS } from "../../../lib/brand";

const FOREST = COLORS.FOREST.hex;
const LINEN = COLORS.LINEN.hex;
const SAND_DARK = "#D9D2C5";
const TEXT_MID = "rgba(28,43,26,0.62)";

export type DeliverableFilterKey =
  | "all"
  | "active"
  | "in_review"
  | "complete"
  | "locked";

export type DeliverableFilterCounts = Record<DeliverableFilterKey, number>;

const FILTERS: Array<{ key: DeliverableFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "in_review", label: "In review" },
  { key: "complete", label: "Complete" },
  { key: "locked", label: "Locked" },
];

export function DeliverableFilterChips({
  active,
  counts,
  onChange,
}: {
  active: DeliverableFilterKey;
  counts: DeliverableFilterCounts;
  onChange: (next: DeliverableFilterKey) => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
      }}
    >
      {FILTERS.map((f) => (
        <Chip
          key={f.key}
          label={f.label}
          count={counts[f.key]}
          isActive={active === f.key}
          onPress={() => onChange(f.key)}
        />
      ))}
    </View>
  );
}

function Chip({
  label,
  count,
  isActive,
  onPress,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onPress: () => void;
}) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Filter: ${label} (${count})`}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          paddingVertical: 8,
          paddingHorizontal: 14,
          borderRadius: 5,
          borderWidth: 1,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          backgroundColor: isActive
            ? FOREST
            : hovered && isWeb
            ? "rgba(28,43,26,0.04)"
            : "transparent",
          borderColor: isActive ? FOREST : SAND_DARK,
        },
        isWeb
          ? ({ cursor: "pointer", transition: "background-color 0.15s, border-color 0.15s" } as object)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: "DMSans_600SemiBold",
          fontSize: 11.5,
          letterSpacing: 0.4,
          color: isActive ? LINEN : TEXT_MID,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 11,
          color: isActive ? LINEN : TEXT_MID,
          opacity: isActive ? 0.7 : 0.55,
          fontVariant: ["tabular-nums"],
        }}
      >
        {count}
      </Text>
    </Pressable>
  );
}
